import React, { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Box } from '@mui/material';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = 'Where should we post your listing?';
export const stepDescription =
  "We'll post your listing to the selected sites within 24 hours.";

interface ListingSite {
  id: string;
  name: string;
  description: string;
  letter: string;
  color: string;
  enabled: boolean;
}

const ApplicationSettings: React.FC = () => {
  const [sites, setSites] = useState<ListingSite[]>([
    {
      id: 'zillow',
      name: 'Zillow',
      description: 'Leading real estate marketplace',
      letter: 'Z',
      color: '#2563eb', // blue-600
      enabled: true,
    },
    {
      id: 'trulia',
      name: 'Trulia',
      description: 'Neighborhood-focused listings',
      letter: 'T',
      color: '#16a34a', // green-600
      enabled: true,
    },
    {
      id: 'realtor',
      name: 'Realtor.com',
      description: 'Official site of NAR',
      letter: 'R',
      color: '#dc2626', // red-600
      enabled: true,
    },
    {
      id: 'apartments',
      name: 'Apartments.com',
      description: 'Rental property specialist',
      letter: 'A',
      color: '#9333ea', // purple-600
      enabled: true,
    },
    {
      id: 'rentals',
      name: 'Rentals.com',
      description: 'Comprehensive rental listings',
      letter: 'R',
      color: '#ea580c', // orange-600
      enabled: true,
    },
    {
      id: 'hotpads',
      name: 'HotPads',
      description: 'Map-based rental search',
      letter: 'H',
      color: '#4f46e5', // indigo-600
      enabled: true,
    },
  ]);

  const handleToggle = (siteId: string) => {
    setSites(
      sites.map(site =>
        site.id === siteId ? { ...site, enabled: !site.enabled } : site
      )
    );
  };

  const enabledSitesCount = sites.filter(site => site.enabled).length;

  return (
    <Box
      sx={{
        maxWidth: 800,
        margin: '0 auto',
        padding: { xs: 2, md: 4 },
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        {sites.map((site, index) => (
          <Box
            key={site.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: { xs: 2, md: 3 },
              transition: 'background-color 0.2s ease',
              borderBottom:
                index < sites.length - 1 ? '1px solid #e0e0e0' : 'none',
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
            }}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  backgroundColor: site.color,
                  borderRadius: 2,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '18px',
                }}
              >
                {site.letter}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='subtitle1'
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    color: 'text.primary',
                  }}
                >
                  {site.name}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.4,
                  }}
                >
                  {site.description}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 24,
                backgroundColor: site.enabled ? '#222222' : '#e0e0e0',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                position: 'relative',
              }}
              onClick={() => handleToggle(site.id)}
              data-testid={`toggle-${site.id}`}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  left: site.enabled ? '26px' : '2px',
                  transition: 'left 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {site.enabled && (
                  <CheckIcon sx={{ fontSize: 12, color: '#222222' }} />
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Info box */}
      <Box
        sx={{
          mt: 4,
          p: 3,
          backgroundColor: '#dbeafe',
          borderRadius: 3,
          border: '1px solid #93c5fd',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CheckIcon sx={{ fontSize: 16, color: '#1e40af' }} />
          <Typography
            variant='subtitle2'
            sx={{
              fontWeight: 600,
              color: '#1e40af',
            }}
          >
            {enabledSitesCount} sites selected
          </Typography>
        </Box>

        <Typography
          variant='body2'
          sx={{
            color: '#1d4ed8',
            lineHeight: 1.5,
          }}
        >
          Your listing will be distributed to all selected platforms within 24
          hours.
        </Typography>
      </Box>
    </Box>
  );
};

export default ApplicationSettings;
